import { Module } from 'vuex';

import { ShiftCMSState } from '@/store';
import {
  User,
  UserAction,
  UserCo2QuizAnswer,
  UserInfoGraphic,
  UserInitiative,
  UserMessage,
  UserGoal,
  UserStep,
  UserStory,
} from '@/models/user.model';
import usersService, { FilterData } from '@/services/users.service';
import { isBefore } from 'date-fns';
import { Category } from '@/models/category.model';
import { UserLabel } from '@/models/label.model';
import { Co2Results } from '@/models/co2-quiz.model';
import { Community, UserCommunity } from '@/models/community.model';
import communityService from '@/services/community.service';
import organizationService from '@/services/organization.service';
import messageService from '@/services/message.service';
import { LoginData } from '@/models/login.model';
import userGoalService from '@/services/user-goal.service';
import { Organization } from '@/models/organization.model';

export interface UsersState {
  userTotal: number;
  user: User | null;
  users: User[];
  communities: Community[];
  organizations: Organization[];
  interests: Category[];
  userStories: UserStory[];
  userLabels: UserLabel[];
  userActions: UserAction[];
  userSteps: UserStep[];
  userInitiatives: UserInitiative[];
  userInfoGraphics: UserInfoGraphic[];
  userCo2QuizAnswers: UserCo2QuizAnswer[];
  userCo2Results: Co2Results[];
  userCommunities: UserCommunity[];
  userMessages: UserMessage[];
  userGoals: UserGoal[];
}

export enum UsersMutations {
  SetUser = 'us/SetUser',
  SetUsers = 'us/SetUsers',
  SetCommunities = 'us/SetCommunities',
  SetOrganizations = 'us/SetOrganizations',
  SetMessages = 'us/SetMessages',
  SetGoals = 'us/SetGoals',
  SetUserTotal = 'us/SetUserTotal',
  SetUserStories = 'us/SetUserStories',
  SetUserLabels = 'us/SetUserLabels',
  SetUserInterests = 'us/SetUserInterests',
  SetUserActions = 'us/SetUserActions',
  SetUserSteps = 'us/SetUserSteps',
  SetUserCommunities = 'us/SetUserCommunities',
  SetUserInitiatives = 'us/SetUserInitiatives',
  SetUserInfoGraphics = 'us/SetUserInfoGraphics',
  SetUserCo2QuizAnswers = 'us/SetUserCo2QuizAnswers',
  SetUserCo2Results = 'us/SetUserCo2Results',
}

export enum UsersActions {
  GetUser = 'us/GetUser',
  GetFullUser = 'us/GetFullUser',
  GetUsers = 'us/GetUsers',
  GetUserCo2Results = 'us/GetUserCo2Results',
  GetUserList = 'us/GetUserList',
  GetCommunitiesList = 'us/GetCommunitiesList',
  GetOrganizationsList = 'us/GetOrganizationsList',
  GetMessages = 'us/GetMessages',
  GetGoals = 'us/GetGoals',
  RemoveUserFromCommunity = 'us/RemoveUserFromCommunity',
  AddUser = 'us/AddUser',
  DeleteUser = 'us/DeleteUser',
  ResetUser = 'us/ResetUser',
  DeleteUserData = 'us/DeleteUserData',
  ResetBPT = 'us/ResetBPT',
  ResetCo2Quiz = 'us/ResetCo2Quiz',
  UpdateUser = 'us/UpdateUser',
  DeleteUserAction = 'us/DeleteUserAction',
  DeleteUserInfoGraphic = 'us/DeleteUserInfoGraphic',
  DeleteUserCo2Answer = 'us/DeleteUserCo2Answer',
  DeleteUserCo2Results = 'us/DeleteUserCo2Results',
  DeleteUserInitiative = 'us/DeleteUserInitiative',
  DeleteUserGoal = 'us/DeleteUserGoal',
  DeleteUserStory = 'us/DeleteUserStory',
  ResetPassword = 'us/ResetPassword',
  Enable2FA = 'us/Enable2FA',
  Enable2FACold = 'us/Enable2FACold',
  Verify2FA = 'us/Verify2FA',
  Verify2FACold = 'us/Verify2FACold',
  Disable2FA = 'us/Disable2FA',
  AddOrganization = 'us/AddOrganization',
  UpdateOrganization = 'us/UpdateOrganization',
  DeleteOrganization = 'us/DeleteOrganization',
}

const sn = (str: UsersActions | UsersMutations): string =>
  str.replace(/us\//g, '');

export const usersStore: Module<UsersState, ShiftCMSState> = {
  namespaced: true,
  state: {
    user: null,
    users: [],
    communities: [],
    organizations: [],
    userTotal: 0,
    interests: [],
    userStories: [],
    userLabels: [],
    userActions: [],
    userSteps: [],
    userInitiatives: [],
    userInfoGraphics: [],
    userCo2QuizAnswers: [],
    userCo2Results: [],
    userCommunities: [],
    userMessages: [],
    userGoals: [],
  },
  mutations: {
    [sn(UsersMutations.SetUser)](state, user: User | null) {
      state.user = user;
    },
    [sn(UsersMutations.SetUsers)](state, users: User[]) {
      state.users = users;
    },
    [sn(UsersMutations.SetCommunities)](state, communities: Community[]) {
      state.communities = communities;
    },
    [sn(UsersMutations.SetOrganizations)](
      state,
      organizations: Organization[],
    ) {
      state.organizations = organizations;
    },
    [sn(UsersMutations.SetMessages)](state, messages: UserMessage[]) {
      state.userMessages = messages;
    },
    [sn(UsersMutations.SetGoals)](state, goals: UserGoal[]) {
      state.userGoals = goals;
    },
    [sn(UsersMutations.SetUserTotal)](state, total: number) {
      state.userTotal = total;
    },
    [sn(UsersMutations.SetUserStories)](state, userStories: UserStory[]) {
      state.userStories = userStories.sort((a, b) =>
        isBefore(a.presentedDate, b.presentedDate) ? -1 : 1,
      );
    },
    [sn(UsersMutations.SetUserLabels)](state, userLabels: UserLabel[]) {
      state.userLabels = userLabels;
    },
    [sn(UsersMutations.SetUserInterests)](state, interests: Category[]) {
      state.interests = interests;
    },
    [sn(UsersMutations.SetUserActions)](state, userActions: UserAction[]) {
      state.userActions = userActions;
    },
    [sn(UsersMutations.SetUserSteps)](state, userSteps: UserStep[]) {
      state.userSteps = userSteps;
    },
    [sn(UsersMutations.SetUserInitiatives)](
      state,
      userInitiatives: UserInitiative[],
    ) {
      state.userInitiatives = userInitiatives;
    },
    [sn(UsersMutations.SetUserInfoGraphics)](
      state,
      userInfoGraphics: UserInfoGraphic[],
    ) {
      state.userInfoGraphics = userInfoGraphics;
    },
    [sn(UsersMutations.SetUserCommunities)](
      state,
      userCommunities: UserCommunity[],
    ) {
      state.userCommunities = userCommunities;
    },
    [sn(UsersMutations.SetUserCo2QuizAnswers)](
      state,
      userCo2QuizAnswers: UserCo2QuizAnswer[],
    ) {
      state.userCo2QuizAnswers = userCo2QuizAnswers;
    },
    [sn(UsersMutations.SetUserCo2Results)](
      state,
      userCo2Results: Co2Results[],
    ) {
      state.userCo2Results = userCo2Results;
    },
  },
  actions: {
    async [sn(UsersActions.GetUser)](_, id) {
      const res = await usersService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(UsersActions.GetFullUser)]({ commit }, id): Promise<User | null> {
      const res = await usersService.getFull(id);
      if (res.status) {
        commit(sn(UsersMutations.SetMessages), []);
        commit(sn(UsersMutations.SetUserCommunities), res.data.communities);
        commit(sn(UsersMutations.SetUserInterests), res.data.interests);
        commit(sn(UsersMutations.SetUserStories), res.data.stories);
        commit(sn(UsersMutations.SetUserLabels), res.data.labels);
        commit(sn(UsersMutations.SetUserActions), res.data.actions);
        commit(sn(UsersMutations.SetUserSteps), res.data.steps);
        commit(sn(UsersMutations.SetUserInitiatives), res.data.initiatives);
        commit(sn(UsersMutations.SetUserInfoGraphics), res.data.infographics);
        commit(
          sn(UsersMutations.SetUserCo2QuizAnswers),
          res.data.userCo2QuizAnswers,
        );

        return res.data;
      }
      return null;
    },
    async [sn(UsersActions.GetUsers)]({ commit }) {
      const res = await usersService.getAll();
      if (res.status) {
        commit(sn(UsersMutations.SetUsers), res.data);
      }
    },
    async [sn(UsersActions.Enable2FA)]() {
      const res = await usersService.enable2FA();
      return res;
    },
    async [sn(UsersActions.Enable2FACold)](_, loginData: LoginData) {
      const res = await usersService.enable2FACold(loginData);
      return res;
    },
    async [sn(UsersActions.Verify2FA)](_, token: string) {
      const res = await usersService.verify2FA(token);

      return res;
    },
    async [sn(UsersActions.Verify2FACold)](
      _,
      data: LoginData & { token: string },
    ) {
      const res = await usersService.verify2FACold(data);

      return res;
    },
    async [sn(UsersActions.Disable2FA)](_, id: number) {
      const res = await usersService.disable2FA(id);

      return res;
    },
    async [sn(UsersActions.GetCommunitiesList)]({ commit }) {
      const res = await communityService.getAll();
      if (res.status) {
        commit(sn(UsersMutations.SetCommunities), res.data);
      }
    },
    async [sn(UsersActions.GetOrganizationsList)]({ commit }) {
      const res = await organizationService.getAll();
      if (res.status) {
        commit(sn(UsersMutations.SetOrganizations), res.data);
      }
    },
    async [sn(UsersActions.AddOrganization)]({ dispatch }, item: Organization) {
      const res = await organizationService.create(item);
      if (res.status) {
        dispatch(sn(UsersActions.GetOrganizationsList));
      }

      return res.status;
    },
    async [sn(UsersActions.UpdateOrganization)](
      { dispatch },
      item: Organization,
    ) {
      const res = await organizationService.edit(item);
      if (res.status) {
        dispatch(sn(UsersActions.GetOrganizationsList));
      }
      return res.status;
    },
    async [sn(UsersActions.DeleteOrganization)]({ dispatch }, id: number) {
      const res = await organizationService.delete(id);

      if (res.status) {
        dispatch(sn(UsersActions.GetOrganizationsList));
      }
    },
    async [sn(UsersActions.GetMessages)]({ commit }, id: number) {
      const res = await messageService.getUserMessages(id);
      if (res.status) {
        commit(sn(UsersMutations.SetMessages), res.data);
      }
    },
    async [sn(UsersActions.GetGoals)]({ commit }, id: number) {
      const res = await userGoalService.getUserGoals(id);
      if (res.status) {
        commit(sn(UsersMutations.SetGoals), res.data);
      }
    },
    async [sn(UsersActions.RemoveUserFromCommunity)](
      { dispatch },
      { id, userId }: { id: number; userId: number },
    ) {
      const res = await communityService.removeUser(id, userId);
      if (res.status) {
        dispatch(sn(UsersActions.GetFullUser), userId);
      }
    },
    async [sn(UsersActions.GetUserCo2Results)]({ commit }, id: number) {
      const res = await usersService.getCo2Results(id);
      if (res.status) {
        commit(sn(UsersMutations.SetUserCo2Results), res.data);
      }
    },
    async [sn(UsersActions.GetUserList)]({ commit }, filters: FilterData) {
      const res = await usersService.getList(filters);
      if (res.status) {
        commit(sn(UsersMutations.SetUsers), res.data.items);
        commit(sn(UsersMutations.SetUserTotal), res.data.total);
      }
    },
    async [sn(UsersActions.AddUser)](_, user: User) {
      const res = await usersService.create({
        ...user,
        id: undefined,
      });

      return res;
    },
    async [sn(UsersActions.DeleteUser)](_, user: User) {
      const res = await usersService.delete(user.id);

      return res.status;
    },
    async [sn(UsersActions.ResetUser)](_, user: User) {
      const res = await usersService.reset(user.id);

      return res.status;
    },
    async [sn(UsersActions.DeleteUserData)](_, user: User) {
      const res = await usersService.deleteData(user.id);

      return res.status;
    },
    async [sn(UsersActions.ResetBPT)]({ dispatch, commit }, user: User) {
      const res = await usersService.resetBPT(user.id);

      commit(sn(UsersMutations.SetUser), res.data);
      dispatch(sn(UsersActions.GetFullUser), user.id);
      return res.status;
    },
    async [sn(UsersActions.ResetCo2Quiz)]({ commit }, user: User) {
      const res = await usersService.ResetCo2Quiz(user.id);

      commit(sn(UsersMutations.SetUser), res.data);
      return res.status;
    },
    async [sn(UsersActions.UpdateUser)](_, user: User) {
      const res = await usersService.edit(user);

      return res;
    },
    async [sn(UsersActions.DeleteUserAction)](
      { dispatch },
      {
        id,
        userId,
        stepIds,
      }: { id: number; userId: number; stepIds: number[] },
    ) {
      for (let index = 0; index < stepIds.length; index++) {
        await usersService.deleteUserStep(stepIds[index]);
      }

      const res = await usersService.deleteUserAction(id);

      dispatch(sn(UsersActions.GetFullUser), userId);

      return res.status;
    },
    async [sn(UsersActions.DeleteUserInfoGraphic)](
      { dispatch },
      { id, userId }: { id: number; userId: number },
    ) {
      const res = await usersService.deleteUserInfoGraphic(id);

      dispatch(sn(UsersActions.GetFullUser), userId);

      return res.status;
    },
    async [sn(UsersActions.DeleteUserCo2Answer)](
      { dispatch },
      { id, userId }: { id: number; userId: number },
    ) {
      const res = await usersService.deleteUserCo2Answer(id);

      dispatch(sn(UsersActions.GetFullUser), userId);

      return res.status;
    },
    async [sn(UsersActions.DeleteUserCo2Results)](
      { dispatch },
      { id, userId }: { id: number; userId: number },
    ) {
      const res = await usersService.deleteUserCo2Results(id, userId);

      dispatch(sn(UsersActions.GetUserCo2Results), userId);

      return res.status;
    },
    async [sn(UsersActions.DeleteUserInitiative)](
      { dispatch },
      { id, userId }: { id: number; userId: number },
    ) {
      const res = await usersService.deleteUserInitiative(id);

      dispatch(sn(UsersActions.GetFullUser), userId);

      return res.status;
    },
    async [sn(UsersActions.DeleteUserGoal)](
      { dispatch },
      { id, userId }: { id: number; userId: number },
    ) {
      const res = await userGoalService.deleteUserGoal(id);

      dispatch(sn(UsersActions.GetGoals), userId);

      return res;
    },
    async [sn(UsersActions.DeleteUserStory)](
      { dispatch },
      { id, userId }: { id: number; userId: number },
    ) {
      const res = await usersService.deleteUserStory(id);

      dispatch(sn(UsersActions.GetFullUser), userId);

      return res.status;
    },
    async [sn(UsersActions.ResetPassword)](
      _,
      { id, password }: { id: number; password: string },
    ) {
      const res = await usersService.resetPassword(id, password);

      return res.status;
    },
  },
};
